import { viewers, avatarColor, avatarInitial, MAX_VIEWERS, AVATAR_COLORS } from '../core/viewers.js';
import { catalog } from '../core/catalog.js';
import { device } from '../core/device.js';
import { t } from '../core/i18n.js';
import { notify } from '../core/notify.js';
import { escapeHtml } from '../core/utils.js';
import { settings } from '../core/storage.js';
import { introActive } from '../core/intro.js';

/* Al abrir la app, si no se elige a nadie, entra solo el último espectador: su avatar lleva un borde que se va llenando y un aviso
   con los segundos. Cualquier toque, tecla o rueda lo cancela. Solo al arrancar (no al «Cambiar de espectador» a propósito). */
export const whoAuto = { pending: false };
const autoSeconds = () => { const v = Number(settings.get('viewerAutoPick')); return isFinite(v) && v >= 0 ? v : 6; };

/* "¿Quién está viendo?": elección y gestión de espectadores (perfiles de persona). */

export function avatarHtml(viewer, cls = '') {
    return `<span class="who-avatar ${cls}" style="background:${avatarColor(viewer)}">${escapeHtml(avatarInitial(viewer))}${viewer && viewer.kids ? `<span class="who-kids">${t('who.kidsBadge')}</span>` : ''}</span>`;
}

/** La tarjeta de un espectador en esta pantalla: fondo teñido de su color, inicial grande con una barra debajo (y «Infantil» si lo es). */
function tileHtml(viewer) {
    return `<span class="who-avatar is-tile" style="--c:${avatarColor(viewer)}"><b class="who-letter">${escapeHtml(avatarInitial(viewer))}</b><i class="who-bar"></i>${viewer && viewer.kids ? `<span class="who-kids">${t('who.kidsBadge')}</span>` : ''}</span>`;
}

function itemsHtml(list, managing) {
    const cur = viewers.current();
    const items = list.map((v) => `<button class="who-item${cur && cur.id === v.id ? ' is-selected' : ''}" data-viewer="${escapeHtml(v.id)}" ${cur && cur.id === v.id ? 'data-nav-default' : ''}>
        ${tileHtml(v)}
        ${managing ? '<span class="who-edit mi">edit</span>' : ''}
        <span class="who-name">${escapeHtml(v.name)}</span>
        <i class="who-dot"></i>
    </button>`);
    if (list.length < MAX_VIEWERS) {
        items.push(`<button class="who-item is-add" data-viewer-add><span class="who-avatar is-tile"><span class="mi">add</span></span><span class="who-name">${t('who.add')}</span><i class="who-dot"></i></button>`);
    }
    return items.join('');
}

export function openViewerEditor(viewer, onDone) {
    const app = window.app;
    const editing = !!viewer;
    const v = Object.assign({ name: '', avatar: viewers.list().length % AVATAR_COLORS.length, kids: false, maxAge: null }, viewer || {});
    const ageValue = v.maxAge == null || v.maxAge === '' ? (v.kids ? '12' : '') : String(v.maxAge);
    const AGES = [['', t('who.maxAgeAny')], ['7', '+7'], ['12', '+12'], ['16', '+16'], ['18', '+18']];
    const content = `<div class="who-editor">
        <div class="who-editor-preview" data-preview>${avatarHtml(v, 'is-large')}</div>
        <div class="field"><label>${t('who.name')}</label><input name="name" value="${escapeHtml(v.name)}" maxlength="30" autocomplete="off" placeholder="${t('who.namePh')}"/></div>
        <div class="field mt-2"><label>${t('who.avatar')}</label>
            <div class="who-swatches">${AVATAR_COLORS.map((c, i) => `<button type="button" class="who-swatch ${i === v.avatar ? 'is-active' : ''}" data-avatar="${i}" style="background:${c}" aria-label="${i + 1}"></button>`).join('')}</div>
        </div>
        <label class="who-kids-toggle mt-3"><input type="checkbox" name="kids" ${v.kids ? 'checked' : ''}/><span><b>${t('who.kids')}</b><small>${t('who.kidsSub')}</small></span></label>
        <div class="field mt-3"><label>${t('who.maxAge')}</label><select name="maxAge">${AGES.map(([val, label]) => `<option value="${val}" ${ageValue === val ? 'selected' : ''}>${label}</option>`).join('')}</select><small class="muted">${t('who.maxAgeSub')}</small></div>
    </div>`;
    const buttons = [];
    if (editing) {
        buttons.push({ text: t('who.clearHistory'), onClick: () => {
            // El editor se cierra antes: Framework7 encola los diálogos y la confirmación no aparecería.
            notify.confirm(t('who.clearHistoryConfirm', { name: v.name }), t('who.clearHistory'), async () => {
                try {
                    await catalog.clearViewerData(v.id);
                    notify.toast(t('who.historyCleared'), { icon: 'check' });
                } catch (e) {
                    notify.error(t('common.errorNetwork'));
                }
            });
        } });
    }
    if (editing && viewers.list().length > 1) {
        buttons.push({ text: t('common.delete'), color: 'red', onClick: () => {
            notify.confirm(t('who.deleteConfirm', { name: v.name }), t('who.delete'), async () => {
                try {
                    await viewers.remove(v.id);
                    await catalog.purgeViewer(v.id);
                    onDone && onDone('deleted');
                } catch (e) {
                    notify.error(t('common.errorNetwork'));
                }
            });
        } });
    }
    buttons.push({ text: t('common.cancel') });
    buttons.push({ text: t('common.save'), bold: true, close: false, onClick: async () => {
        const el = dialog.$el[0];
        const name = el.querySelector('input[name=name]').value.trim();
        if (!name) {
            el.querySelector('input[name=name]').focus();
            return;
        }
        const ageSel = el.querySelector('select[name=maxAge]');
        const data = { name, avatar: v.avatar, kids: el.querySelector('input[name=kids]').checked, maxAge: ageSel && ageSel.value !== '' ? Number(ageSel.value) : null };
        try {
            const saved = editing ? await viewers.update(v.id, data) : await viewers.add(data);
            dialog.close();
            onDone && onDone(editing ? 'updated' : 'added', saved);
        } catch (e) {
            notify.error(e.message === 'limit' ? t('who.limit') : t('common.errorNetwork'));
        }
    } });
    const dialog = app.dialog.create({
        title: editing ? t('who.edit') : t('who.new'),
        content,
        cssClass: 'who-dialog',
        buttons,
        on: {
            opened() {
                const el = dialog.$el[0];
                el.querySelector('.who-swatches').addEventListener('click', (e) => {
                    const sw = e.target.closest('[data-avatar]');
                    if (!sw) return;
                    v.avatar = Number(sw.getAttribute('data-avatar'));
                    el.querySelectorAll('.who-swatch').forEach((b) => b.classList.toggle('is-active', b === sw));
                    el.querySelector('[data-preview]').innerHTML = avatarHtml(Object.assign({}, v, { name: el.querySelector('input[name=name]').value, kids: el.querySelector('input[name=kids]').checked }), 'is-large');
                });
                el.querySelector('input[name=name]').addEventListener('input', (e) => {
                    el.querySelector('[data-preview]').innerHTML = avatarHtml(Object.assign({}, v, { name: e.target.value, kids: el.querySelector('input[name=kids]').checked }), 'is-large');
                });
                // Al marcar "infantil" sin límite elegido, se propone +12.
                el.querySelector('input[name=kids]').addEventListener('change', (e) => {
                    const sel = el.querySelector('select[name=maxAge]');
                    if (e.target.checked && sel && sel.value === '') sel.value = '12';
                });
                if (!device.isTV) el.querySelector('input[name=name]').focus();
                else window.IPTV.tvnav.focusLayer(el);
            }
        }
    });
    dialog.open();
    return dialog;
}

export default {
    path: '/who/',
    nav: null,

    render() {
        return `<div class="page no-nav" data-name="who">
            <div class="page-content" style="padding:0">
                <div class="who">
                    <div class="who-brand"><img class="who-logo" src="img/logo.png" alt=""/><span>${t('app.name')}</span></div>
                    <h1 class="who-title">${t('who.title')}</h1>
                    <p class="who-tagline">${t('who.tagline')}</p>
                    <div class="who-grid" data-nav-row data-grid>${itemsHtml(viewers.list(), false)}</div>
                    <button type="button" class="btn who-manage" data-manage><span class="mi">edit</span>${t('who.manage')}</button>
                </div>
            </div>
        </div>`;
    },

    mount(page) {
        const root = page.el;
        const grid = root.querySelector('[data-grid]');
        const manageBtn = root.querySelector('[data-manage]');
        const state = { managing: false };
        let auto = null; // { cancel }

        const cancelAuto = () => { if (auto) { auto.cancel(); auto = null; } };
        const startAuto = () => {
            const secs = autoSeconds();
            const viewer = viewers.current();
            if (!secs || !viewer || !viewers.get(viewer.id)) return;
            const item = grid.querySelector('[data-viewer="' + (window.CSS && CSS.escape ? CSS.escape(viewer.id) : viewer.id) + '"]');
            if (!item) return;
            item.querySelector('.who-avatar').insertAdjacentHTML('beforeend', '<svg class="who-ring" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><rect class="who-ring-track" x="1" y="1" width="98" height="98" rx="12" ry="10"/><rect class="who-ring-fill" x="1" y="1" width="98" height="98" rx="12" ry="10" pathLength="100" style="animation-duration:' + secs + 's"/></svg>');
            const hint = document.createElement('p');
            hint.className = 'who-auto';
            hint.setAttribute('role', 'status');
            root.querySelector('.who-tagline').insertAdjacentElement('afterend', hint);
            const t0 = Date.now();
            const paintHint = () => { hint.textContent = t('who.autoIn', { name: viewer.name, n: Math.max(0, Math.ceil(secs - (Date.now() - t0) / 1000)) }); };
            paintHint();
            const tick = setInterval(paintHint, 250);
            const fill = item.querySelector('.who-ring-fill');
            const stopEvents = () => ['pointerdown', 'keydown', 'wheel'].forEach((ev) => document.removeEventListener(ev, cancelAuto, true));
            const cleanup = () => { clearInterval(tick); stopEvents(); const ring = item.querySelector('.who-ring'); if (ring) ring.remove(); hint.remove(); };
            const done = () => {
                cleanup();
                auto = null;
                if (!grid.classList.contains('is-busy') && !state.managing) item.click();
            };
            fill.addEventListener('animationend', done, { once: true });
            ['pointerdown', 'keydown', 'wheel'].forEach((ev) => document.addEventListener(ev, cancelAuto, true));
            auto = { cancel: () => { fill.removeEventListener('animationend', done); cleanup(); } };
        };
        // La animación de arranque va por encima: la cuenta atrás empieza cuando acaba.
        const whenCalm = (fn) => { const tick = () => { if (!root.isConnected) return; if (introActive()) setTimeout(tick, 150); else fn(); }; tick(); };

        const paint = () => {
            grid.innerHTML = itemsHtml(viewers.list(), state.managing);
            root.querySelector('.who').classList.toggle('is-managing', state.managing);
            manageBtn.innerHTML = state.managing ? `<span class="mi">check</span>${t('who.done')}` : `<span class="mi">edit</span>${t('who.manage')}`;
            if (device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(root), 60);
        };

        // Una sola tarjeta destacada a la vez: la del espectador actual al entrar, y la que se señala con el ratón o el mando.
        const select = (item) => {
            if (!item) return;
            grid.querySelectorAll('.who-item.is-selected').forEach((x) => { if (x !== item) x.classList.remove('is-selected'); });
            item.classList.add('is-selected');
        };
        grid.addEventListener('mouseover', (e) => select(e.target.closest('.who-item')));
        grid.addEventListener('nav:focus', (e) => select(e.target.closest('.who-item')));
        grid.addEventListener('focusin', (e) => select(e.target.closest('.who-item')));

        manageBtn.addEventListener('click', () => {
            cancelAuto();
            state.managing = !state.managing;
            paint();
        });

        grid.addEventListener('click', async (e) => {
            const add = e.target.closest('[data-viewer-add]');
            if (add) {
                openViewerEditor(null, (what, saved) => {
                    paint();
                    if (what === 'added' && saved && !state.managing) window.IPTV.actions.selectViewer(saved.id);
                });
                return;
            }
            const btn = e.target.closest('[data-viewer]');
            if (!btn) return;
            const id = btn.getAttribute('data-viewer');
            if (state.managing) {
                openViewerEditor(viewers.get(id), () => paint());
                return;
            }
            // Respuesta inmediata al toque: se marca y se bloquea mientras se abre el Inicio.
            // Otra tarjeta sí se puede elegir mientras tanto (si el servidor tarda, la rejilla no se queda bloqueada); la misma, no.
            if (btn.classList.contains('is-chosen')) return;
            grid.classList.add('is-busy');
            btn.classList.add('is-chosen');
            try { await window.IPTV.actions.selectViewer(id); } finally { btn.classList.remove('is-chosen'); if (!grid.querySelector('.is-chosen')) grid.classList.remove('is-busy'); }
        });

        if (device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(root), 100);
        if (whoAuto.pending) { whoAuto.pending = false; whenCalm(startAuto); }
        return () => { cancelAuto(); };
    }
};
